import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Tabs, Tag, Button, Table, Modal, Form, Input, Select, Space, Collapse,
  Progress, message, Tooltip, Badge, Popconfirm, DatePicker, Empty, Descriptions, Timeline
} from 'antd';
import {
  ArrowLeftOutlined, PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined,
  LinkOutlined, SafetyCertificateOutlined, CloudServerOutlined, BookOutlined,
  FlagOutlined, RocketOutlined, BugOutlined, SafetyOutlined,
  SoundOutlined, AuditOutlined, ReloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';
import ProjectServerRoles from '../components/ProjectServerRoles';
import { buildDirectServerRows, collectDirectServerIds } from '../utils/projectDirectServers';
import { getProyectoComoFunciona, getProyectoResumen } from '../data/proyectoDocs';
import {
  useViewProyectoDetalleQuery,
  useListProjectsQuery,
  useAddProyectoServidorMutation, useDeleteProyectoServidorMutation,
  useAddRoadmapTareaMutation, useEditRoadmapTareaMutation, useDeleteRoadmapTareaMutation,
  useAddBitacoraMutation, useDeleteBitacoraMutation,
  useListServersQuery,
  useAddUrlMonitorMutation, useDeleteUrlMonitorMutation, useCheckSingleUrlMutation,
  useAddSslMonitorMutation, useDeleteSslMonitorMutation, useCheckSingleSslMutation,
} from '../store/api';

const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;

const catIcons = {
  mejora: <RocketOutlined />, bug: <BugOutlined />, feature: <FlagOutlined />,
  infraestructura: <CloudServerOutlined />, seguridad: <SafetyOutlined />,
  marketing: <SoundOutlined />, legal: <AuditOutlined />,
};
const catColors = {
  mejora: 'blue', bug: 'red', feature: 'green', infraestructura: 'purple',
  seguridad: 'orange', marketing: 'cyan', legal: 'gold',
};
const prioColors = { critica: '#f5222d', alta: '#fa8c16', media: '#1890ff', baja: '#8c8c8c' };
const estadoColors = { pendiente: 'default', en_progreso: 'processing', completada: 'success', cancelada: 'error' };
const estadoLabels = { pendiente: 'Pendiente', en_progreso: 'En Progreso', completada: 'Completada', cancelada: 'Cancelada' };

const ProyectoDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: proyectoDetalle, isLoading, refetch } = useViewProyectoDetalleQuery(id, { pollingInterval: 30000 });
  const { data: proyectosList = [], refetch: refetchList } = useListProjectsQuery();
  const proyectoFromList = proyectosList.find(p => String(p.id) === String(id));
  const proyecto = proyectoDetalle ? { ...proyectoFromList, ...proyectoDetalle } : proyectoFromList;
  const { data: allServers = [] } = useListServersQuery(undefined, { pollingInterval: 60000 });

  const refreshAll = () => { refetch(); refetchList(); };
  const [addServidor] = useAddProyectoServidorMutation();
  const [deleteServidor] = useDeleteProyectoServidorMutation();
  const [addTarea] = useAddRoadmapTareaMutation();
  const [editTarea] = useEditRoadmapTareaMutation();
  const [deleteTarea] = useDeleteRoadmapTareaMutation();
  const [addBitacora] = useAddBitacoraMutation();
  const [deleteBitacora] = useDeleteBitacoraMutation();
  const [addUrlMonitor] = useAddUrlMonitorMutation();
  const [deleteUrlMonitor] = useDeleteUrlMonitorMutation();
  const [checkUrl] = useCheckSingleUrlMutation();
  const [addSslMonitor] = useAddSslMonitorMutation();
  const [deleteSslMonitor] = useDeleteSslMonitorMutation();
  const [checkSsl] = useCheckSingleSslMutation();

  const [tareaModal, setTareaModal] = useState(false);
  const [editingTarea, setEditingTarea] = useState(null);
  const [srvModal, setSrvModal] = useState(false);
  const [bitacoraModal, setBitacoraModal] = useState(false);
  const [urlModal, setUrlModal] = useState(false);
  const [sslModal, setSslModal] = useState(false);
  const [tareaForm] = Form.useForm();
  const [srvForm] = Form.useForm();
  const [bitacoraForm] = Form.useForm();
  const [urlForm] = Form.useForm();
  const [sslForm] = Form.useForm();

  if (isLoading) return <div className="page-container fade-in"><Card loading /></div>;
  if (!proyecto) return <div className="page-container fade-in"><Empty description="Proyecto no encontrado" /></div>;

  const directServerRows = buildDirectServerRows(proyecto, allServers);
  const directCount = collectDirectServerIds(proyecto).length;
  const resumen = getProyectoResumen(proyecto);
  const comoFunciona = getProyectoComoFunciona(proyecto);

  const roadmapStats = proyecto.roadmap_stats || {};
  const totalTareas = Object.values(roadmapStats).reduce((a, b) => a + parseInt(b || 0), 0);
  const completadas = parseInt(roadmapStats.completada || 0);
  const progreso = totalTareas > 0 ? Math.round((completadas / totalTareas) * 100) : 0;

  // ── Handlers ──
  const saveTarea = async () => {
    try {
      const values = await tareaForm.validateFields();
      const payload = { ...values, proyecto_id: id, fecha_limite: values.fecha_limite?.format('YYYY-MM-DD') || null };
      if (editingTarea) {
        await editTarea({ id: editingTarea.id, ...payload }).unwrap();
        message.success('Tarea actualizada');
      } else {
        await addTarea(payload).unwrap();
        message.success('Tarea creada');
      }
      setTareaModal(false); setEditingTarea(null); tareaForm.resetFields(); refetch();
    } catch { message.error('Error al guardar tarea'); }
  };

  const toggleTareaEstado = async (tarea) => {
    const next = tarea.estado === 'completada' ? 'pendiente' : 'completada';
    await editTarea({ id: tarea.id, estado: next }).unwrap();
    message.success(next === 'completada' ? 'Tarea completada' : 'Tarea reabierta');
    refetch();
  };

  const saveServidor = async () => {
    try {
      const values = await srvForm.validateFields();
      await addServidor({ proyecto_id: id, ...values }).unwrap();
      message.success('Servidor asociado'); setSrvModal(false); srvForm.resetFields(); refetch();
    } catch { message.error('Error'); }
  };

  const saveBitacora = async () => {
    try {
      const values = await bitacoraForm.validateFields();
      await addBitacora({ proyecto_id: id, ...values }).unwrap();
      message.success('Entrada creada'); setBitacoraModal(false); bitacoraForm.resetFields(); refetch();
    } catch { message.error('Error'); }
  };

  const saveUrl = async () => {
    try {
      const values = await urlForm.validateFields();
      await addUrlMonitor({ proyecto_id: id, ...values }).unwrap();
      message.success('Monitor URL creado'); setUrlModal(false); urlForm.resetFields(); refetch();
    } catch { message.error('Error'); }
  };

  const saveSsl = async () => {
    try {
      const values = await sslForm.validateFields();
      await addSslMonitor({ proyecto_id: id, ...values }).unwrap();
      message.success('Monitor SSL creado'); setSslModal(false); sslForm.resetFields(); refetch();
    } catch { message.error('Error'); }
  };

  // ── Roadmap Tab ──
  const RoadmapTab = () => {
    const tareas = proyecto.roadmap || [];
    const grouped = { pendiente: [], en_progreso: [], completada: [], cancelada: [] };
    tareas.forEach(t => { (grouped[t.estado] || grouped.pendiente).push(t); });

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Progress type="circle" percent={progreso} size={60} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 16 }}>{completadas} / {totalTareas} tareas</div>
              <div style={{ color: '#8c8c8c', fontSize: 12 }}>
                {parseInt(roadmapStats.en_progreso || 0)} en progreso • {parseInt(roadmapStats.pendiente || 0)} pendientes
              </div>
            </div>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingTarea(null); tareaForm.resetFields(); setTareaModal(true); }}>
            Nueva Tarea
          </Button>
        </div>

        {['en_progreso', 'pendiente', 'completada', 'cancelada'].map(estado => {
          if (!grouped[estado]?.length) return null;
          return (
            <div key={estado} style={{ marginBottom: 24 }}>
              <h4 style={{ marginBottom: 8 }}>
                <Badge status={estadoColors[estado]} /> {estadoLabels[estado]} ({grouped[estado].length})
              </h4>
              <Collapse accordion expandIconPosition="start">
                {grouped[estado].map(tarea => (
                  <Panel
                    key={tarea.id}
                    header={
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                        <Tooltip title={tarea.estado === 'completada' ? 'Reabrir' : 'Completar'}>
                          <Button size="small" type={tarea.estado === 'completada' ? 'primary' : 'default'}
                            icon={<CheckOutlined />} shape="circle"
                            onClick={e => { e.stopPropagation(); toggleTareaEstado(tarea); }}
                            style={tarea.estado === 'completada' ? { background: '#52c41a', border: 'none' } : {}}
                          />
                        </Tooltip>
                        <Tag color={catColors[tarea.categoria]} icon={catIcons[tarea.categoria]}>{tarea.categoria}</Tag>
                        <span style={{ fontWeight: 500, textDecoration: tarea.estado === 'completada' ? 'line-through' : 'none', flex: 1 }}>
                          {tarea.titulo}
                        </span>
                        <Tag style={{ borderColor: prioColors[tarea.prioridad], color: prioColors[tarea.prioridad] }}>{tarea.prioridad}</Tag>
                        {tarea.fecha_limite && <Tag>{tarea.fecha_limite}</Tag>}
                      </div>
                    }
                    extra={
                      <Space size={4} onClick={e => e.stopPropagation()}>
                        <Button size="small" icon={<EditOutlined />} onClick={() => {
                          setEditingTarea(tarea);
                          tareaForm.setFieldsValue({ ...tarea, fecha_limite: tarea.fecha_limite ? dayjs(tarea.fecha_limite) : null });
                          setTareaModal(true);
                        }} />
                        <Popconfirm title="¿Eliminar?" onConfirm={async () => { await deleteTarea(tarea.id).unwrap(); refetch(); }}>
                          <Button size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    }
                  >
                    {tarea.descripcion && <p style={{ color: '#595959', marginBottom: 12 }}>{tarea.descripcion}</p>}
                    {tarea.herramientas && (
                      <div style={{ marginBottom: 12 }}>
                        <strong>Herramientas: </strong>
                        {tarea.herramientas.split(',').map((h, i) => <Tag key={i} color="blue">{h.trim()}</Tag>)}
                      </div>
                    )}
                    {tarea.instrucciones ? (
                      <Card size="small" title="📋 Instrucciones paso a paso" style={{ background: '#fafafa' }}>
                        <div className="markdown-content">
                          <ReactMarkdown>{tarea.instrucciones}</ReactMarkdown>
                        </div>
                      </Card>
                    ) : (
                      <Empty description="Sin instrucciones detalladas" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )}
                  </Panel>
                ))}
              </Collapse>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Servers Tab ──
  const ServersTab = () => (
    <div>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 12 }}>
        Métricas en vivo desde el listado de servidores (carga/RAM/disco). Roles de infra + asociaciones extra.
      </p>
      <Table dataSource={directServerRows} rowKey="id" size="small" pagination={false} style={{ marginBottom: 20 }}
        columns={[
          { title: 'Servidor', dataIndex: 'nombre', render: (t, r) => <span><CloudServerOutlined style={{ marginRight: 6 }} />{t} <span style={{ color: '#64748b' }}>({r.ip || 'sin IP'})</span></span> },
          { title: 'Roles directos', dataIndex: 'roles', render: r => <Tag color="blue">{r}</Tag> },
          { title: 'Carga', dataIndex: 'carga' },
          { title: 'RAM', dataIndex: 'ram' },
          { title: 'Disco libre', dataIndex: 'espaciolibre' },
        ]}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { srvForm.resetFields(); setSrvModal(true); }}>Asociar Servidor (extra)</Button>
      </div>
      <Table dataSource={proyecto.servidores || []} rowKey="id" size="small" pagination={false}
        columns={[
          { title: 'Servidor', dataIndex: 'servidor_nombre', render: (t, r) => <span><CloudServerOutlined style={{ marginRight: 6 }} />{t} ({(r.ip || '').trim()})</span> },
          { title: 'Rol', dataIndex: 'rol', render: r => <Tag color="blue">{r}</Tag> },
          { title: 'Notas', dataIndex: 'notas' },
          { title: '', key: 'act', width: 50, render: (_, r) => (
            <Popconfirm title="¿Desasociar?" onConfirm={async () => { await deleteServidor(r.id).unwrap(); refetch(); }}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )},
        ]}
      />
    </div>
  );

  // ── URL Monitor Tab ──
  const UrlMonitorTab = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { urlForm.resetFields(); setUrlModal(true); }}>Agregar URL</Button>
      </div>
      <Table dataSource={proyecto.url_monitors || []} rowKey="id" size="small" pagination={false}
        columns={[
          { title: 'Nombre', dataIndex: 'nombre' },
          { title: 'URL', dataIndex: 'url', render: u => <a href={u} target="_blank" rel="noreferrer">{u?.substring(0, 50)}</a> },
          { title: 'Estado', dataIndex: 'ultimo_estado', render: s => <Tag color={s === 'up' ? 'green' : s === 'down' ? 'red' : 'default'}>{s?.toUpperCase()}</Tag> },
          { title: 'Tiempo', dataIndex: 'ultimo_tiempo_ms', render: t => t ? `${t}ms` : '-' },
          { title: 'Uptime 24h', dataIndex: 'uptime_24h', render: u => u ? `${u}%` : '-' },
          { title: 'Último check', dataIndex: 'ultimo_check', render: d => d ? dayjs(d).format('DD/MM HH:mm') : '-' },
          { title: '', key: 'act', width: 80, render: (_, r) => (
            <Space size={4}>
              <Button size="small" icon={<ReloadOutlined />} onClick={async () => { await checkUrl(r.id).unwrap(); message.success('Verificado'); refetch(); }} />
              <Popconfirm title="¿Eliminar?" onConfirm={async () => { await deleteUrlMonitor(r.id).unwrap(); refetch(); }}>
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Space>
          )},
        ]}
      />
    </div>
  );

  // ── SSL Monitor Tab ──
  const SslMonitorTab = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { sslForm.resetFields(); setSslModal(true); }}>Agregar Dominio</Button>
      </div>
      <Table dataSource={proyecto.ssl_monitors || []} rowKey="id" size="small" pagination={false}
        columns={[
          { title: 'Dominio', dataIndex: 'dominio' },
          { title: 'Estado', dataIndex: 'estado', render: s => {
            const c = { valido: 'green', por_vencer: 'orange', vencido: 'red', error: 'default' };
            return <Tag color={c[s]}>{s?.toUpperCase()}</Tag>;
          }},
          { title: 'Días restantes', dataIndex: 'dias_restantes', render: d => d != null ? d : '-' },
          { title: 'Emisor', dataIndex: 'emisor' },
          { title: 'Vence', dataIndex: 'valido_hasta', render: d => d ? dayjs(d).format('DD/MM/YYYY') : '-' },
          { title: '', key: 'act', width: 80, render: (_, r) => (
            <Space size={4}>
              <Button size="small" icon={<ReloadOutlined />} onClick={async () => { await checkSsl(r.id).unwrap(); message.success('Verificado'); refetch(); }} />
              <Popconfirm title="¿Eliminar?" onConfirm={async () => { await deleteSslMonitor(r.id).unwrap(); refetch(); }}>
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Space>
          )},
        ]}
      />
    </div>
  );

  // ── Bitácora Tab ──
  const BitacoraTab = () => {
    const tipoColors = { nota: 'blue', incidente: 'red', mantenimiento: 'orange', cambio: 'green', deploy: 'purple' };
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { bitacoraForm.resetFields(); setBitacoraModal(true); }}>Nueva Entrada</Button>
        </div>
        <Timeline>
          {(proyecto.bitacora || []).map(b => (
            <Timeline.Item key={b.id} color={tipoColors[b.tipo] || 'gray'}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <Tag color={tipoColors[b.tipo]}>{b.tipo}</Tag>
                  <strong>{b.titulo}</strong>
                  <span style={{ color: '#8c8c8c', marginLeft: 8, fontSize: 12 }}>— {b.username} • {dayjs(b.created_at).format('DD/MM/YYYY HH:mm')}</span>
                </div>
                <Popconfirm title="¿Eliminar?" onConfirm={async () => { await deleteBitacora(b.id).unwrap(); refetch(); }}>
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </div>
              {b.contenido && <p style={{ marginTop: 4, color: '#595959' }}>{b.contenido}</p>}
            </Timeline.Item>
          ))}
        </Timeline>
        {(!proyecto.bitacora || proyecto.bitacora.length === 0) && <Empty description="Sin entradas en la bitácora" />}
      </div>
    );
  };

  return (
    <div className="page-container fade-in">
      <div className="page-header">
        <div className="page-header-left">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/proyectos')} style={{ marginRight: 12 }} />
          <div>
            <h1 className="page-title" style={{ margin: 0 }}>
              <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: proyecto.color || '#845adf', marginRight: 8 }} />
              {proyecto.nombre}
            </h1>
            <div className="page-subtitle">
              {proyecto.tech_stack && <Tag color="purple">{proyecto.tech_stack}</Tag>}
              <Tag color={proyecto.estado === 'Completado' ? 'green' : proyecto.estado === 'En Progreso' ? 'blue' : 'orange'}>{proyecto.estado}</Tag>
              {proyecto.url_produccion && <a href={proyecto.url_produccion} target="_blank" rel="noreferrer"><LinkOutlined /> {proyecto.url_produccion}</a>}
            </div>
          </div>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={refetch}>Actualizar</Button>
        </Space>
      </div>

      <Card size="small" style={{ marginBottom: 16 }} title="Qué es y cómo funciona">
        <p style={{ margin: '0 0 12px', fontSize: 15, lineHeight: 1.55 }}>{resumen}</p>
        <Tag color="geekblue" style={{ marginBottom: 12 }}>
          {directCount} servidor{directCount !== 1 ? 'es' : ''} implicado{directCount !== 1 ? 's' : ''} directamente
        </Tag>
        {comoFunciona ? (
          <div className="markdown-content" style={{ background: '#f8fafc', padding: 12, borderRadius: 8 }}>
            <ReactMarkdown>{comoFunciona}</ReactMarkdown>
          </div>
        ) : (
          <span style={{ color: '#94a3b8' }}>Sin detalle de funcionamiento — edítalo en Proyectos → Editar (campo «Cómo funciona»).</span>
        )}
        <Descriptions size="small" column={1} style={{ marginTop: 16 }}>
          <Descriptions.Item label="Función (BD)">
            {proyecto.descripcion || <span style={{ color: '#94a3b8' }}>Sin descripción</span>}
          </Descriptions.Item>
        </Descriptions>
        <Descriptions size="small" column={2} style={{ marginTop: 8 }}>
          <Descriptions.Item label="URL producción">
            {proyecto.url_produccion
              ? <a href={proyecto.url_produccion} target="_blank" rel="noreferrer">{proyecto.url_produccion}</a>
              : <span style={{ color: '#94a3b8' }}>—</span>}
          </Descriptions.Item>
          <Descriptions.Item label="URL administrativa">
            {proyecto.url_admin
              ? <a href={proyecto.url_admin} target="_blank" rel="noreferrer">{proyecto.url_admin}</a>
              : <span style={{ color: '#94a3b8' }}>—</span>}
          </Descriptions.Item>
          <Descriptions.Item label="Credenciales" span={2}>
            {proyecto.credenciales_acceso
              ? <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>{proyecto.credenciales_acceso}</pre>
              : <span style={{ color: '#94a3b8' }}>—</span>}
          </Descriptions.Item>
          {proyecto.url_staging && <Descriptions.Item label="Staging"><a href={proyecto.url_staging} target="_blank" rel="noreferrer">{proyecto.url_staging}</a></Descriptions.Item>}
          {proyecto.repositorio && <Descriptions.Item label="Repositorio"><a href={proyecto.repositorio} target="_blank" rel="noreferrer">{proyecto.repositorio}</a></Descriptions.Item>}
          <Descriptions.Item label="Inicio">{proyecto.fecha_inicio || '—'}</Descriptions.Item>
          {proyecto.manual_agente && <Descriptions.Item label="Manual agente" span={2}>
            {proyecto.manual_agente.startsWith('http') ? <a href={proyecto.manual_agente} target="_blank" rel="noreferrer">{proyecto.manual_agente}</a> : <code>{proyecto.manual_agente}</code>}
          </Descriptions.Item>}
        </Descriptions>
      </Card>

      <ProjectServerRoles proyecto={proyecto} onUpdated={refreshAll} />

      <Card bordered={false}>
        <Tabs defaultActiveKey="roadmap" items={[
          { key: 'roadmap', label: <span><FlagOutlined /> Roadmap ({totalTareas})</span>, children: <RoadmapTab /> },
          { key: 'servidores', label: <span><CloudServerOutlined /> Servidores ({directCount})</span>, children: <ServersTab /> },
          { key: 'urls', label: <span><LinkOutlined /> URLs ({(proyecto.url_monitors || []).length})</span>, children: <UrlMonitorTab /> },
          { key: 'ssl', label: <span><SafetyCertificateOutlined /> SSL ({(proyecto.ssl_monitors || []).length})</span>, children: <SslMonitorTab /> },
          { key: 'bitacora', label: <span><BookOutlined /> Bitácora ({(proyecto.bitacora || []).length})</span>, children: <BitacoraTab /> },
        ]} />
      </Card>

      {/* ── Tarea Modal ── */}
      <Modal title={editingTarea ? 'Editar Tarea' : 'Nueva Tarea'} open={tareaModal} onOk={saveTarea}
        onCancel={() => { setTareaModal(false); setEditingTarea(null); tareaForm.resetFields(); }}
        okText="Guardar" width={700}>
        <Form form={tareaForm} layout="vertical">
          <Form.Item name="titulo" label="Título" rules={[{ required: true }]}><Input /></Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Form.Item name="categoria" label="Categoría" initialValue="mejora">
              <Select>{['mejora','bug','feature','infraestructura','seguridad','marketing','legal'].map(c => <Option key={c} value={c}>{c}</Option>)}</Select>
            </Form.Item>
            <Form.Item name="prioridad" label="Prioridad" initialValue="media">
              <Select>{['critica','alta','media','baja'].map(p => <Option key={p} value={p}>{p}</Option>)}</Select>
            </Form.Item>
            <Form.Item name="estado" label="Estado" initialValue="pendiente">
              <Select>{['pendiente','en_progreso','completada','cancelada'].map(e => <Option key={e} value={e}>{estadoLabels[e]}</Option>)}</Select>
            </Form.Item>
          </div>
          <Form.Item name="descripcion" label="Descripción"><TextArea rows={2} /></Form.Item>
          <Form.Item name="herramientas" label="Herramientas (separadas por coma)"><Input placeholder="Git, Node.js, Docker..." /></Form.Item>
          <Form.Item name="fecha_limite" label="Fecha límite"><DatePicker style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="instrucciones" label="Instrucciones paso a paso (Markdown)">
            <TextArea rows={8} placeholder="### Paso 1&#10;Describir la acción...&#10;&#10;### Paso 2&#10;Siguiente acción..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Servidor Modal ── */}
      <Modal title="Asociar Servidor" open={srvModal} onOk={saveServidor}
        onCancel={() => { setSrvModal(false); srvForm.resetFields(); }} okText="Asociar">
        <Form form={srvForm} layout="vertical">
          <Form.Item name="servidor_id" label="Servidor" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children">{allServers.map(s => <Option key={s.id} value={s.id}>{s.nombre} ({s.ip})</Option>)}</Select>
          </Form.Item>
          <Form.Item name="rol" label="Rol" initialValue="produccion">
            <Select>{['produccion','staging','desarrollo','base_datos','backup'].map(r => <Option key={r} value={r}>{r}</Option>)}</Select>
          </Form.Item>
          <Form.Item name="notas" label="Notas"><Input /></Form.Item>
        </Form>
      </Modal>

      {/* ── Bitácora Modal ── */}
      <Modal title="Nueva Entrada Bitácora" open={bitacoraModal} onOk={saveBitacora}
        onCancel={() => { setBitacoraModal(false); bitacoraForm.resetFields(); }} okText="Guardar">
        <Form form={bitacoraForm} layout="vertical">
          <Form.Item name="titulo" label="Título" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="tipo" label="Tipo" initialValue="nota">
            <Select>{['nota','incidente','mantenimiento','cambio','deploy'].map(t => <Option key={t} value={t}>{t}</Option>)}</Select>
          </Form.Item>
          <Form.Item name="contenido" label="Contenido"><TextArea rows={4} /></Form.Item>
        </Form>
      </Modal>

      {/* ── URL Monitor Modal ── */}
      <Modal title="Agregar Monitor URL" open={urlModal} onOk={saveUrl}
        onCancel={() => { setUrlModal(false); urlForm.resetFields(); }} okText="Crear">
        <Form form={urlForm} layout="vertical">
          <Form.Item name="nombre" label="Nombre"><Input /></Form.Item>
          <Form.Item name="url" label="URL" rules={[{ required: true }]}><Input placeholder="https://..." /></Form.Item>
          <Form.Item name="metodo" label="Método" initialValue="GET">
            <Select>{['GET','HEAD','POST'].map(m => <Option key={m} value={m}>{m}</Option>)}</Select>
          </Form.Item>
          <Form.Item name="esperado_http" label="HTTP esperado" initialValue={200}><Input type="number" /></Form.Item>
        </Form>
      </Modal>

      {/* ── SSL Monitor Modal ── */}
      <Modal title="Agregar Monitor SSL" open={sslModal} onOk={saveSsl}
        onCancel={() => { setSslModal(false); sslForm.resetFields(); }} okText="Crear">
        <Form form={sslForm} layout="vertical">
          <Form.Item name="dominio" label="Dominio" rules={[{ required: true }]}><Input placeholder="ejemplo.com" /></Form.Item>
          <Form.Item name="puerto" label="Puerto" initialValue={443}><Input type="number" /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProyectoDetalle;
